import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import InfeedAd from '../components/InfeedAd';
import DisplayAd from '../components/DisplayAd';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import '../styles/quizesPage.css';

export default function QuizesPage() {
  const formatDate = (timestamp) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const { user } = useAuth();
  const { subjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [quizzes, setQuizzes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deletingQuizId, setDeletingQuizId] = useState(null);

  // Get subject info from location state
  const subjectName = location.state?.subjectName || 'All Quizzes';
  const subjectNameMarathi = location.state?.subjectNameMarathi || 'सर्व प्रश्नसंच';
  const apiSubject = location.state?.apiSubject || 'all';

  // ✅ Fetch quizzes by subject using API
  const fetchQuizzesBySubject = async (page = 1) => {
    setLoading(true);
    try {
      let response;

      if (apiSubject === 'all') {
        // Fetch all quizzes
        response = await quizAPI.getAllQuizzes(page, 15);
      } else {
        // ✅ Fetch quizzes by subject using your API
        response = await quizAPI.getQuizzesBySubject(apiSubject, page, 15);
      }

      // console.log('API Response:', response.data); // ✅ Debug log

      if (response.data.success) {
        setQuizzes(response.data.quizzes || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || page);
        setTotalQuizzes(response.data.totalQuizzes || 0);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      setQuizzes([]);
      setTotalQuizzes(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId, quizSubject, quizDate, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete the quiz?\n\nSubject: ${quizSubject}\nDate: ${formatDate(quizDate)}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingQuizId(quizId);
      const response = await quizAPI.deleteQuiz(quizId);

      if (response.data) {
        alert('Quiz deleted successfully!');
        fetchQuizzesBySubject(currentPage);
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert(error.response?.data?.message || 'Failed to delete quiz. Please try again.');
    } finally {
      setDeletingQuizId(null);
    }
  };

  useEffect(() => {
    fetchQuizzesBySubject(currentPage);
  }, [currentPage, apiSubject]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="quizes-page">
      <Helmet>
        <title>{subjectName} - Free MPSC Test Series | Spardhaweb</title>
        <meta name="description" content={`Practice ${subjectName} quizzes for MPSC preparation. Free test series with detailed solutions.`} />
        <meta name="keywords" content={`${subjectName}, MPSC test series, MPSC preparation, free tests`} />
      </Helmet>

      <Navbar />

      {/* Header Section */}
      <div className="quizzes-header">
        <div className="container">
          <button 
            onClick={() => navigate('/subjects')}
            className="btn-back"
          >
            ← Back to Subjects
          </button>
          <h1 className="quizzes-heading">{subjectName}</h1>
          <p className="quizzes-subheading">{subjectNameMarathi}</p>
          <div className="quizzes-meta">
            <span className="meta-item">
              📝 {totalQuizzes} Quizzes Available
            </span>
            <span className="meta-item">
              📅 Updated {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 quizzes-container">
            <DisplayAd />

            {loading ? (
              <Spinner />
            ) : quizzes.length === 0 ? (
              <div className="no-quizzes-container">
                <div className="no-quizzes-icon">📝</div>
                <h3>No Quizzes Available</h3>
                <p>No quizzes found for {subjectName}. Check back later!</p>
                <button 
                  onClick={() => navigate('/subjects')}
                  className="btn btn-primary mt-3"
                >
                  Browse Other Subjects
                </button>
              </div>
            ) : (
              <div className="container my-3 flex-grow-1">
                <div className="row">
                  {quizzes.map((quiz, index) => (
                    <React.Fragment key={quiz._id}>
                      <div className="col-md-4">
                        <div className="my-3">
                          <div className="quiz-card-wrapper">
                            <Link to={`/start/${quiz._id}`} className="card-link">
                              <div className="card quiz-card">
                                <img 
                                  src="https://res.cloudinary.com/dde4uttp5/image/upload/v1767805760/shared_image_10_q2gsfk.jpg" 
                                  className="card-img-top" 
                                  alt="Quiz" 
                                />
                                <div className="card-body">
                                  <h5 className="card-title">📅 {formatDate(quiz.date)}</h5>
                                  <h5 className="card-subject">📚 {quiz.caption}</h5>
                                  <div className="quiz-actions">
                                    <span className="btn btn-sm btn-dark start-quiz-btn">
                                      Start Test →
                                    </span>
                                  </div>
                                  <p className="card-text my-2">
                                    <small className="text-muted">
                                      By team@spardhaweb on {new Date(quiz.date).toUTCString()}
                                    </small>
                                  </p>
                                </div>
                              </div>
                            </Link>

                            {/* Admin Delete Button */}
                            {user?.role === 'admin' && (
                              <button
                                className="btn-delete-quiz"
                                onClick={(e) => handleDeleteQuiz(quiz._id, quiz.caption, quiz.date, e)}
                                disabled={deletingQuizId === quiz._id}
                                title="Delete Quiz"
                              >
                                {deletingQuizId === quiz._id ? (
                                  <span className="spinner-small"></span>
                                ) : (
                                  '🗑️'
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Insert Ad after every 6 quizzes */}
                      {index !== 0 && (index + 1) % 6 === 0 && (
                        <div className="col-md-4">
                          <InfeedAd />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {quizzes.length > 0 && totalPages > 1 && (
              <div className="d-flex justify-content-center my-4">
                <button 
                  onClick={handlePrevPage} 
                  disabled={currentPage === 1} 
                  className="btn btn-primary me-3"
                >
                  ← Previous
                </button>
                <span className="align-self-center pagination-info">
                  Page {currentPage} of {totalPages} ({totalQuizzes} total)
                </span>
                <button 
                  onClick={handleNextPage} 
                  disabled={currentPage === totalPages} 
                  className="btn btn-primary ms-3"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}